import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';

// (9 Oct 2026) Settings > Calendar Options (decision 52), saved with the footer button
// like Profile and Poll Settings (decision 60): nothing is saved until "Save Calendar
// Options" is pressed; until then the tab carries the amber unsaved dot and the footer
// reads "Save Calendar Options" (otherwise "Saved ✓"). Replaces HostBoardSettingsTab.jsx
// and CulinaryHistoryOption.jsx, which saved as they went.
//
// Two parts:
//  1. Hosting schedule: on/off (a tick box; turning it on or off asks for confirmation
//     when Save is pressed) and, while it is on, its two timings:
//       - "Ask the host to reconfirm": days before the meeting the GA is prompted to ask
//         the chosen host to reconfirm (default 30);
//       - "Time for the host to reply": days after that request before silence counts
//         as a yes (default 7).
//     Timings: GET/PATCH .../host-board/settings (refused while the board is off, so
//     they appear once the schedule is on and saved).
//  2. Calendar history (8.19/8.20; generalised 9 Oct 2026, decisions 64-67): on/off; its
//     keyword (e.g. "Culinary provenance:"; blank: every event is in the history) and its
//     History qualifier (was "Index column"; e.g. "Country"; blank: none; offered only
//     once there is a keyword), both fixed once saved; whether
//     the History page shows the menu (dishes); and its curator (decision 59), anyone on
//     the calendar who may correct the History page alongside the administrator and the
//     stand-in. PATCH /api/calendars/:id.
//  3. (26 Oct 2026, decision 145) Within the hosting schedule, while it is on: "Confirmed
//     hosts are Event Administrators" (calendars.ea_enabled, PATCH /api/calendars/:id).
//  4. (26 Oct 2026, decision 146) Dishes: "Dishes are a surprise until the event day"
//     (calendars.hide_dishes) and, while ticked, "Members see the dishes"
//     (calendars.dish_reveal: 'event_day' | 'event_start' (default) | 'day_after').
//     Independent of the Event Administrator option. PATCH /api/calendars/:id.
// Only the calendar's current active holder can change these (the server says so too).
// Exposes save() and reports { hasChanges, isSaving } to SettingsModal, as
// PollSettingsTab does. onSaved(fields) tells App.js what changed on the calendar.

// (10 Oct 2026, late; replaces decision 73's wording) The captions under Keyword and
// History qualifier (was "Index column"; the database names history_index_label and
// index_value are unchanged). The warnings are repeated in the confirmation on Save.
// The History qualifier is shown only once a Keyword is entered (or one is saved),
// since it qualifies the keyword's value (decision 74).
const KEYWORD_WARNING = 'Once saved, this keyword cannot be changed.';
const INDEX_WARNING = 'Once saved, this qualifier cannot be changed.';
const KEYWORD_CAPTION = "Use this keyword to extract history data from the event's 'Notes' field: when the Notes begin with the keyword, the text after it is displayed under a column heading of the keyword itself. A new line terminates the data and, once saved, this keyword cannot be changed.";
// (26 Oct 2026, decision 145) Neil's wording; kept general on purpose, so the Event
// Administrator's scope can change later without rewording the screens and the guide.
const EA_LABEL = 'Confirmed hosts are Event Administrators';
const EA_CAPTION = 'As the Event Administrator, they see every poll response and dish, can answer for members and open or close the poll. Event changes such as date/time remain with the Group Administrator.';
// (26 Oct 2026, decision 146) Neil's wording.
const HIDE_LABEL = 'Dishes are a surprise until the event day';
const HIDE_CAPTION = 'Members see the course and who is bringing each dish; the administrators see the descriptions.';
const REVEAL_OPTIONS = [
  { value: 'event_day', label: 'On the day of the event' },
  { value: 'event_start', label: 'When the event starts' },
  { value: 'day_after', label: 'The day after the event' },
];
const INDEX_CAPTION = `An additional history qualifier for the keyword's data (e.g. the country of a region), entered when an event is created or edited, and displayed under a column heading of the History qualifier itself. ${INDEX_WARNING}`;

const CalendarOptionsTab = forwardRef(({ currentCalendar, apiBaseUrl, authToken, onStatusMessage, onStateChange, onSaved }, ref) => {
  const calendarId = currentCalendar ? currentCalendar.id : null;
  const savedBoardOn = !!currentCalendar && currentCalendar.host_board_enabled === true;
  const savedHistoryOn = !!currentCalendar && currentCalendar.history_enabled === true;
  const savedKeyword = (currentCalendar && currentCalendar.history_keyword ? currentCalendar.history_keyword : '').trim();
  const savedIndexLabel = (currentCalendar && currentCalendar.history_index_label ? currentCalendar.history_index_label : '').trim();
  const savedShowMenu = !!currentCalendar && currentCalendar.history_show_menu === true;
  const savedEaOn = !!currentCalendar && currentCalendar.ea_enabled === true; // (decision 145)
  const savedHideDishes = !!currentCalendar && currentCalendar.hide_dishes === true; // (decision 146)
  const savedReveal = (currentCalendar && REVEAL_OPTIONS.some((o) => o.value === currentCalendar.dish_reveal))
    ? currentCalendar.dish_reveal : 'event_start';

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [canManage, setCanManage] = useState(false);
  const [bounds, setBounds] = useState({ reconfirmDays: [7, 90], responseDays: [2, 14] });
  const [savedDays, setSavedDays] = useState(null); // { reconfirmDays, responseDays } while the board is on
  const [savedCurator, setSavedCurator] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [draft, setDraft] = useState({
    boardOn: savedBoardOn, reconfirmDays: '', responseDays: '', historyOn: savedHistoryOn, curator: '',
    keyword: savedKeyword, indexLabel: savedIndexLabel, showMenu: savedShowMenu, eaOn: savedEaOn,
    hideDishes: savedHideDishes, reveal: savedReveal,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const headers = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${authToken || localStorage.getItem('auth_token')}`,
  });

  useEffect(() => {
    if (!calendarId) return undefined;
    let cancelled = false;
    const getJson = async (url) => {
      const r = await fetch(url, { headers: headers() });
      return { ok: r.ok, d: await r.json().catch(() => null) };
    };
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const [board, cur] = await Promise.all([
          savedBoardOn
            ? getJson(`${apiBaseUrl}/api/calendars/${calendarId}/host-board/settings`)
            : getJson(`${apiBaseUrl}/api/calendars/${calendarId}/host-board`),
          getJson(`${apiBaseUrl}/api/calendars/${calendarId}/curator-candidates`),
        ]);
        if (cancelled) return;
        if (!board.ok || !board.d) {
          setLoadError((board.d && board.d.error) || 'Could not load the calendar options');
          return;
        }
        setCanManage(board.d.canManage === true);
        let days = null;
        if (savedBoardOn) {
          days = { reconfirmDays: board.d.reconfirmDays, responseDays: board.d.responseDays };
          if (board.d.bounds) setBounds(board.d.bounds);
        }
        setSavedDays(days);
        const curatorEmail = cur.ok && cur.d ? (cur.d.curatorEmail || '') : '';
        setSavedCurator(curatorEmail);
        setCandidates(cur.ok && cur.d ? (cur.d.candidates || []) : []);
        setDraft({
          boardOn: savedBoardOn,
          reconfirmDays: days ? String(days.reconfirmDays) : '',
          responseDays: days ? String(days.responseDays) : '',
          historyOn: savedHistoryOn,
          curator: curatorEmail,
          keyword: savedKeyword,
          indexLabel: savedIndexLabel,
          showMenu: savedShowMenu,
          eaOn: savedEaOn,
          hideDishes: savedHideDishes,
          reveal: savedReveal,
        });
      } catch (_) {
        if (!cancelled) setLoadError('Could not load the calendar options');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarId, apiBaseUrl, savedBoardOn, savedHistoryOn, savedKeyword, savedIndexLabel, savedShowMenu, savedEaOn, savedHideDishes, savedReveal, reloadToken]);

  const parse = (text) => (/^\d+$/.test(String(text).trim()) ? Number(text) : NaN);
  const dayProblem = (key) => {
    if (!savedDays || !draft.boardOn) return null;
    const v = parse(draft[key]);
    const [min, max] = bounds[key];
    return !Number.isInteger(v) || v < min || v > max ? `This must be a whole number from ${min} to ${max}.` : null;
  };
  // The History qualifier is offered only once there is a keyword (typed or saved); a
  // qualifier saved earlier stays visible regardless, so it can always be seen.
  const hasKeyword = !!savedKeyword || draft.keyword.trim() !== '';
  const showQualifier = draft.historyOn && (hasKeyword || !!savedIndexLabel);
  const daysChanged = !!savedDays && draft.boardOn &&
    (parse(draft.reconfirmDays) !== savedDays.reconfirmDays || parse(draft.responseDays) !== savedDays.responseDays);
  const hasChanges = !loading && !loadError && (
    draft.boardOn !== savedBoardOn ||
    daysChanged ||
    draft.historyOn !== savedHistoryOn ||
    (draft.historyOn && draft.curator !== savedCurator) ||
    (draft.historyOn && !savedKeyword && draft.keyword.trim() !== '') ||
    (draft.historyOn && hasKeyword && !savedIndexLabel && draft.indexLabel.trim() !== '') ||
    (draft.historyOn && draft.showMenu !== savedShowMenu) ||
    (draft.boardOn && draft.eaOn !== savedEaOn) ||
    draft.hideDishes !== savedHideDishes ||
    (draft.hideDishes && draft.reveal !== savedReveal)
  );

  useEffect(() => {
    if (typeof onStateChange === 'function') onStateChange({ hasChanges, isSaving });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasChanges, isSaving]);

  const save = async () => {
    if (!hasChanges || isSaving) return;
    if (!canManage) {
      onStatusMessage("Only the calendar's current organiser can change these settings.", 'error', 5000);
      return;
    }
    const problem = dayProblem('reconfirmDays') || dayProblem('responseDays');
    if (problem && daysChanged) {
      onStatusMessage(problem, 'error', 5000);
      return;
    }
    if (draft.boardOn !== savedBoardOn) {
      const question = draft.boardOn
        ? 'Turn on the hosting schedule? Members will be able to see the schedule and offer to host. You can turn it off again at any time.'
        : 'Turn off the hosting schedule? Members will no longer see it, and the daily reminders stop. Any reminders waiting in your Notifications are hidden until you turn it back on. Nothing is deleted.';
      if (!window.confirm(question)) return;
    }
    // (decisions 64, 65, 67) The keyword and the History qualifier are fixed once saved.
    // A qualifier is saved only alongside a keyword (typed now or saved before).
    const newKeyword = draft.historyOn && !savedKeyword ? draft.keyword.trim() : '';
    const newIndexLabel = draft.historyOn && hasKeyword && !savedIndexLabel ? draft.indexLabel.trim() : '';
    if (newKeyword && !window.confirm(`Save the keyword "${newKeyword}"?\n\n${KEYWORD_WARNING}`)) return;
    if (newIndexLabel && !window.confirm(`Save the history qualifier "${newIndexLabel}"?\n\n${INDEX_WARNING}`)) return;
    setIsSaving(true);
    try {
      const calendarFields = {};
      if (draft.boardOn !== savedBoardOn) calendarFields.host_board_enabled = draft.boardOn;
      if (draft.historyOn !== savedHistoryOn) calendarFields.history_enabled = draft.historyOn;
      if (draft.historyOn && draft.curator !== savedCurator) calendarFields.history_curator_email = draft.curator || null;
      if (newKeyword) calendarFields.history_keyword = newKeyword;
      if (newIndexLabel) calendarFields.history_index_label = newIndexLabel;
      if (draft.historyOn && draft.showMenu !== savedShowMenu) calendarFields.history_show_menu = draft.showMenu;
      if (draft.boardOn && draft.eaOn !== savedEaOn) calendarFields.ea_enabled = draft.eaOn; // (decision 145)
      if (draft.hideDishes !== savedHideDishes) calendarFields.hide_dishes = draft.hideDishes; // (decision 146)
      if (draft.hideDishes && draft.reveal !== savedReveal) calendarFields.dish_reveal = draft.reveal;
      if (Object.keys(calendarFields).length > 0) {
        const res = await fetch(`${apiBaseUrl}/api/calendars/${calendarId}`, {
          method: 'PATCH', headers: headers(), body: JSON.stringify(calendarFields),
        });
        const d = await res.json().catch(() => null);
        if (!res.ok) {
          onStatusMessage((d && d.error) || 'Could not save the calendar options', 'error', 5000);
          return;
        }
      }
      if (daysChanged) {
        const body = {};
        if (parse(draft.reconfirmDays) !== savedDays.reconfirmDays) body.reconfirmDays = parse(draft.reconfirmDays);
        if (parse(draft.responseDays) !== savedDays.responseDays) body.responseDays = parse(draft.responseDays);
        const res = await fetch(`${apiBaseUrl}/api/calendars/${calendarId}/host-board/settings`, {
          method: 'PATCH', headers: headers(), body: JSON.stringify(body),
        });
        const d = await res.json().catch(() => null);
        if (!res.ok) {
          onStatusMessage((d && d.error) || 'Could not save the hosting schedule timings', 'error', 5000);
          return;
        }
      }
      onStatusMessage('Calendar options saved', 'success');
      const changed = {};
      if (calendarFields.host_board_enabled !== undefined) changed.host_board_enabled = calendarFields.host_board_enabled;
      ['history_enabled', 'history_keyword', 'history_index_label', 'history_show_menu', 'ea_enabled', 'hide_dishes', 'dish_reveal'].forEach((k) => {
        if (calendarFields[k] !== undefined) changed[k] = calendarFields[k];
      });
      if (Object.keys(changed).length > 0 && typeof onSaved === 'function') onSaved(changed);
      // Reload what was saved (turning the schedule on makes its timings available).
      setReloadToken((t) => t + 1);
    } catch (_) {
      onStatusMessage('Could not save the calendar options', 'error', 5000);
    } finally {
      setIsSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({ save }));

  if (loading) return <div className="text-center py-8 text-gray-500">Loading...</div>;
  if (loadError) return <div className="text-sm text-gray-600">{loadError}</div>;

  const inputClass = (bad) =>
    `w-24 px-3 py-2 border-2 rounded-lg focus:outline-none disabled:bg-gray-100 ${
      bad ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-blue-500'
    }`;
  const reconfirmProblem = dayProblem('reconfirmDays');
  const responseProblem = dayProblem('responseDays');
  // A curator chosen earlier who has since left the calendar is still shown, so the
  // choice can be seen and changed.
  const listed = candidates.some((c) => c.email === draft.curator);

  return (
    <div className="space-y-8">
      {!canManage && (
        <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
          Only the calendar's current organiser can change these settings.
        </p>
      )}

      {/* ---------- 1. Hosting schedule ---------- */}
      <div className="space-y-5">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Hosting Schedule</h3>
          <p className="text-sm text-gray-500">Plan which day each month's meeting is on, and who is hosting.</p>
        </div>
        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={draft.boardOn}
            disabled={!canManage}
            onChange={(e) => setDraft({ ...draft, boardOn: e.target.checked })}
            className="mt-1 w-4 h-4"
          />
          <span>
            <span className="block text-sm font-semibold text-gray-700">Use a hosting schedule</span>
            <span className="block text-xs text-gray-500 mt-1">
              Members see the schedule and can offer to host. Turning it off hides it and stops the reminders:
              nothing is deleted, and every month, offer and host comes back when it is turned on again.
            </span>
          </span>
        </label>

        {draft.boardOn && !savedDays && (
          <p className="text-xs text-gray-500">The schedule's reminder timings appear here once it has been turned on and saved.</p>
        )}
        {draft.boardOn && savedDays && (
          <>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1" htmlFor="co-reconfirm-days">
                Ask the host to reconfirm
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  id="co-reconfirm-days"
                  type="number"
                  inputMode="numeric"
                  min={bounds.reconfirmDays[0]}
                  max={bounds.reconfirmDays[1]}
                  value={draft.reconfirmDays}
                  disabled={!canManage}
                  onChange={(e) => setDraft({ ...draft, reconfirmDays: e.target.value })}
                  className={inputClass(Boolean(reconfirmProblem))}
                />
                <span className="text-sm text-gray-600">days before the meeting</span>
              </div>
              {reconfirmProblem ? (
                <p className="text-xs text-red-600 mt-1">{reconfirmProblem}</p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  About this long before a meeting, you will get a notification with a message ready to send to the chosen host.
                  {` (${bounds.reconfirmDays[0]} to ${bounds.reconfirmDays[1]} days.)`}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1" htmlFor="co-response-days">
                Time for the host to reply
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  id="co-response-days"
                  type="number"
                  inputMode="numeric"
                  min={bounds.responseDays[0]}
                  max={bounds.responseDays[1]}
                  value={draft.responseDays}
                  disabled={!canManage}
                  onChange={(e) => setDraft({ ...draft, responseDays: e.target.value })}
                  className={inputClass(Boolean(responseProblem))}
                />
                <span className="text-sm text-gray-600">days after you send the request</span>
              </div>
              {responseProblem ? (
                <p className="text-xs text-red-600 mt-1">{responseProblem}</p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  If the host has neither confirmed nor stepped down by then, they are confirmed as host automatically and you are told.
                  {` (${bounds.responseDays[0]} to ${bounds.responseDays[1]} days.)`}
                </p>
              )}
            </div>
          </>
        )}

        {/* (26 Oct 2026, decision 145) Only with a hosting schedule: it is the schedule
            that confirms an event's host. */}
        {draft.boardOn && (
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={draft.eaOn}
              disabled={!canManage}
              onChange={(e) => setDraft({ ...draft, eaOn: e.target.checked })}
              className="mt-1 w-4 h-4"
            />
            <span>
              <span className="block text-sm font-semibold text-gray-700">{EA_LABEL}</span>
              <span className="block text-xs text-gray-500 mt-1">{EA_CAPTION}</span>
            </span>
          </label>
        )}
      </div>

      {/* ---------- (26 Oct 2026, decision 146) Dishes ---------- */}
      <div className="border-t border-gray-200 pt-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Dishes</h3>
        </div>
        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={draft.hideDishes}
            disabled={!canManage}
            onChange={(e) => setDraft({ ...draft, hideDishes: e.target.checked })}
            className="mt-1 w-4 h-4"
          />
          <span>
            <span className="block text-sm font-semibold text-gray-700">{HIDE_LABEL}</span>
            <span className="block text-xs text-gray-500 mt-1">{HIDE_CAPTION}</span>
          </span>
        </label>
        {draft.hideDishes && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1" htmlFor="co-dish-reveal">Members see the dishes</label>
            <select
              id="co-dish-reveal"
              value={draft.reveal}
              disabled={!canManage}
              onChange={(e) => setDraft({ ...draft, reveal: e.target.value })}
              className="w-full sm:w-80 px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:outline-none disabled:bg-gray-100 bg-white"
            >
              {REVEAL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* ---------- 2. Calendar history ---------- */}
      <div className="border-t border-gray-200 pt-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Calendar history</h3>
          <p className="text-sm text-gray-500">
            A History page listing the calendar's meetings: when, who hosted and, if you choose, what was on the menu.
          </p>
        </div>
        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={draft.historyOn}
            disabled={!canManage}
            onChange={(e) => setDraft({ ...draft, historyOn: e.target.checked })}
            className="mt-1 w-4 h-4"
          />
          <span>
            <span className="block text-sm font-semibold text-gray-700">Keep a calendar history</span>
            <span className="block text-xs text-gray-500 mt-1">
              Members and the administrator see a History button. Turning this off only hides
              the History button: nothing is deleted.
            </span>
          </span>
        </label>

        {draft.historyOn && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1" htmlFor="co-keyword">Keyword</label>
            <input
              id="co-keyword"
              type="text"
              maxLength={60}
              value={draft.keyword}
              placeholder="For example: Culinary provenance:"
              disabled={!canManage || !!savedKeyword}
              onChange={(e) => setDraft({ ...draft, keyword: e.target.value })}
              className="w-full sm:w-80 px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:outline-none disabled:bg-gray-100"
            />
            {/* (10 Oct 2026) One caption, before and after saving. */}
            <p className="text-xs text-gray-500 mt-1">{KEYWORD_CAPTION}</p>
          </div>
        )}

        {showQualifier && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1" htmlFor="co-index-label">History qualifier</label>
            <input
              id="co-index-label"
              type="text"
              maxLength={40}
              value={draft.indexLabel}
              placeholder="For example: Country"
              disabled={!canManage || !!savedIndexLabel}
              onChange={(e) => setDraft({ ...draft, indexLabel: e.target.value })}
              className="w-full sm:w-80 px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:outline-none disabled:bg-gray-100"
            />
            {/* (10 Oct 2026) One caption, before and after saving. */}
            <p className="text-xs text-gray-500 mt-1">{INDEX_CAPTION}</p>
          </div>
        )}

        {draft.historyOn && (
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={draft.showMenu}
              disabled={!canManage}
              onChange={(e) => setDraft({ ...draft, showMenu: e.target.checked })}
              className="mt-1 w-4 h-4"
            />
            <span>
              <span className="block text-sm font-semibold text-gray-700">Show the menu (dishes)</span>
              <span className="block text-xs text-gray-500 mt-1">
                The History page shows each meeting's starter, main and dessert, from its poll. Without it, it shows the date and the host.
              </span>
            </span>
          </label>
        )}

        {draft.historyOn && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1" htmlFor="co-curator">Curator</label>
            <select
              id="co-curator"
              value={draft.curator}
              disabled={!canManage}
              onChange={(e) => setDraft({ ...draft, curator: e.target.value })}
              className="w-full sm:w-auto px-3 py-2 border-2 border-gray-200 rounded-lg bg-white text-sm focus:border-blue-500 focus:outline-none disabled:bg-gray-100"
            >
              <option value="">No curator</option>
              {!listed && draft.curator && <option value={draft.curator}>{draft.curator} (no longer on this calendar)</option>}
              {candidates.map((c) => <option key={c.email} value={c.email}>{c.label}</option>)}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              The curator can correct the History page: menus, hosts and dates. The administrator and the stand-in can too.
            </p>
          </div>
        )}
      </div>
    </div>
  );
});

export default CalendarOptionsTab;
